import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Autocomplete, InputAdornment, Paper, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';

export const CustomPaperStyled = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})(({ theme, fullWidth }) => ({
  backgroundColor: theme.palette.colors.secondaryBackground,
  border: `solid 1px ${theme.palette.colors.mainFocused}`,

  marginTop: '2px',
  boxShadow: 'none',
  padding: 8,

  width: fullWidth ? '100%' : '400px',
  [theme.breakpoints.down('lg')]: {
    width: fullWidth ? '100%' : '344px',
    marginBottom: '2px',
  },
  [theme.breakpoints.down('sm')]: {
    width: fullWidth ? '100%' : '302px',
  },

  '& ul': {
    padding: 0,
  },
  '& .MuiAutocomplete-noOptions': {
    padding: 0,
  },
  '& .MuiAutocomplete-listbox .MuiAutocomplete-option': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '32px',
    width: '100%',
    backgroundColor: theme.palette.colors.primaryBackground,
    paddingRight: '6px',
    paddingLeft: '8px',
    borderRadius: '5px',

    '&:not(:last-child)': {
      marginBottom: '4px',
    },
    '&:hover': {
      backgroundColor: theme.palette.colors.selectedAndDetailBackground,
    },
  },
  '& .MuiAutocomplete-listbox': {
    '@-moz-document url-prefix()': {
      scrollbarColor: `${theme.palette.colors.scrollOnPrimaryBackground} transparent`,
      scrollbarWidth: 'thin',
    },
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
      webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.colors.scrollOnPrimaryBackground,
      borderRadius: '5px',
    },
  },
}));

export const CustomAutocomplete = styled(Autocomplete, {
  shouldForwardProp: (prop) => prop !== 'fullWidth' && prop !== 'isSearchBar',
})(({ theme, fullWidth, isSearchBar }) => ({
  marginRight: !fullWidth && '8px',

  [theme.breakpoints.between('lg', 'xl')]: {
    ...(isSearchBar && {
      position: 'absolute',
      top: '74px',
    }),
  },
  '& .MuiAutocomplete-noOptions': {
    padding: 0,
  },
  '& .MuiAutocomplete-endAdornment': {
    display: 'none',
  },
}));

export const NoText = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: '16px',
  fontSize: '12px',
  fontWeight: 500,
  color: theme.palette.colors.onSecondaryAndDetailBackground,
  backgroundColor: theme.palette.colors.selectedAndDetailBackground,
}));

export const CircleXIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  color: theme.palette.colors.highlight,
  width: '16px',
  height: '14px',
  marginRight: '8px',
}));

export const CustomTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== 'fullWidth' && prop !== 'uppercaseLetters',
})(({ theme, fullWidth, uppercaseLetters }) => ({
  border: 'none',
  borderRadius: '5px',

  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      border: `solid 1px ${theme.palette.colors.mainFocused}`,
    },
    '&.Mui-focused fieldset': {
      border: `solid 1px ${theme.palette.colors.mainFocused}`,
    },
  },
  '& .MuiInputBase-root': {
    width: fullWidth ? '100%' : '272px',
    height: '40px',
    padding: '0 !important',
    backgroundColor: theme.palette.colors.secondaryBackground,

    [theme.breakpoints.up('md')]: {
      width: fullWidth ? '100%' : '531px',
    },
    [theme.breakpoints.up('lg')]: {
      width: fullWidth ? '100%' : '272px',
      height: '32px',
    },
  },
  '& input': {
    padding: '0 !important',
    backgroundColor: theme.palette.colors.secondaryBackground,
    fontSize: '12px',
    border: 'none',
    color: theme.palette.colors.onSecondaryAndDetailBackground,
    caretColor: theme.palette.colors.mainFocused,
    textTransform: uppercaseLetters && 'uppercase',
    '& [type="search"]::-webkit-search-cancel-button,[type="search"]::-webkit-search-decoration':
      {
        WebkitAppearance: 'none',
        appearance: 'none',
      },
    '&::placeholder': {
      color: theme.palette.colors.placeholderText,
      fontSize: '12px',
      fontStyle: 'italic',
      opacity: 1,
    },
  },
  '& fieldset': {
    border: `solid 1px ${theme.palette.colors.borderLine}`,
    padding: 0,
  },
}));

export const SearchIconContainer = styled(InputAdornment)(({ theme }) => ({
  height: '24px',
  width: '14px',
  marginLeft: '8px',
  color: theme.palette.colors.mainFocused,
  backgroundColor: theme.palette.colors.secondaryBackground,
}));

export const SearchIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  color: theme.palette.colors.mainFocused,
  width: '14px',
  height: '24px',
  fontSize: '14px',
}));

export const WrapperIconText = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hasLeftStraip',
})(({ theme, hasLeftStraip }) => ({
  display: 'flex',
  alignItems: 'center',
  ...(hasLeftStraip && {
    '&::before': {
      content: `''`,
      position: 'absolute',
      backgroundColor: theme.palette.colors.mainFocused,
      width: '4px',
      height: '100%',
      bottom: 0,
      left: 0,
      borderTopLeftRadius: '5px',
      borderBottomLeftRadius: '5px',
    },
  }),
}));

export const TextWrapper = styled('span', {
  shouldForwardProp: (prop) =>
    prop !== 'isSecondary' && prop !== 'uppercaseLetters',
})(({ theme, isSecondary, uppercaseLetters }) => ({
  textTransform: uppercaseLetters && 'uppercase',
  color: theme.palette.colors.onSecondaryAndDetailBackground,
  fontSize: '12px',
  marginLeft: isSecondary ? '13px' : '8px',
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',

  width: '250px',
  [theme.breakpoints.down('lg')]: {
    width: '194px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '152px',
  },
}));
